/**
 * Server-side structured data for the RaiseCreditScoreBeforeMortgage tool page.
 * Lives in a data module (not the page component) so routeMeta can emit it
 * into the server HTML; react-helmet would only add it after hydration.
 */
export const FAQS = [
  {
    id: "mortgage-fico-models",
    q: "Which credit scores do mortgage lenders actually use?",
    a: "Most conventional mortgage lenders pull a tri-merge report using older-generation FICO models: FICO Score 2 from Experian, FICO Score 5 from Equifax, and FICO Score 4 from TransUnion. Lenders generally use the middle of those three, and when two borrowers are on the loan they typically use the lower of the two middle scores. Free apps usually show a newer FICO version or a VantageScore, which is why the number you see there can differ from the number a lender sees.",
  },
  {
    id: "azeo-timing",
    q: "What is AZEO and when should I do it?",
    a: "AZEO stands for all zero except one: you let a single credit card report a small balance while every other revolving account reports zero. Because most issuers report the statement balance, the timing that matters is the statement closing date, not the payment due date. If you want the pattern reflected in a lender's pull, it generally needs to be in place for the statement cycle that closes before that pull. Whether it changes your score, and by how much, depends on your individual file.",
  },
  {
    id: "experian-boost",
    q: "Does Experian Boost help with a mortgage?",
    a: "Boost adds utility, rent, or streaming payment history to a single bureau's file. The older FICO models used in mortgage underwriting may not factor that data in, and the other two bureaus are unaffected. It may help scores shown in consumer apps while doing nothing for the scores your lender pulls, so it shouldn't be the foundation of a mortgage-prep plan.",
  },
  {
    id: "rapid-rescoring",
    q: "What is rapid rescoring and can I request it myself?",
    a: "Rapid rescoring is a lender-initiated process that asks the bureaus to update already-corrected information faster than the normal reporting cycle. You cannot order it directly — it goes through your loan officer, and it requires documentation from the creditor showing the corrected information. It updates accurate data faster; it does not remove accurate negative information, and there is no assurance it changes your score.",
  },
  {
    id: "collections",
    q: "Should I pay off old collections before applying?",
    a: "It depends. Newer scoring models often disregard paid collections, but the older mortgage models may still count them, and paying can sometimes update the account's activity date. Separately, some loan programs and underwriters require certain collections to be resolved regardless of the scoring effect. Ask your loan officer to look at the specific accounts before you send any money.",
  },
  {
    id: "statement-closing-dates",
    q: "Why does the statement closing date matter more than the due date?",
    a: "Card issuers typically report your balance to the bureaus at the statement closing date. If you carry a large balance through that date and pay it in full by the due date a few weeks later, you pay no interest but the bureaus still recorded the high balance. Paying down before the statement closes is what changes the reported utilization.",
  },
];

export const PAGE_URL = "https://www.homeapproach.com/tools/raise-credit-score-before-mortgage";

  export const RAISE_CREDIT_SCORE_BEFORE_MORTGAGE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "How to Raise Your Credit Score Fast Before a Mortgage",
      url: PAGE_URL,
      description:
        "What actually moves the credit scores mortgage lenders pull, on a 30, 60, and 90-day timeline \u2014 mortgage FICO models, AZEO and statement timing, rapid rescoring, report errors, collections, and the moves that backfire.",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: "https://www.homeapproach.com" },
      publisher: { "@type": "Organization", name: "Home Approach", url: "https://www.homeapproach.com" },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Raise Your Credit Score Before a Mortgage",
          item: PAGE_URL,
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
